import { useEffect, useRef, useState } from "react";
import { cn } from "./cn.ts";

export type AdSlotFormat = "leaderboard" | "rectangle" | "responsive";

/**
 * Heights per format, applied once the ad is live so the slot does not jump
 * while the creative loads. Before that the component renders nothing at all:
 * an empty reserved box reads as a broken page, and the one-time shift when a
 * visitor grants consent happens on their own click, which CLS does not count.
 *
 * The reserve is given back the moment AdSense reports `unfilled` — see
 * useAdFilled. Google does not always collapse an unfilled slot itself, and a
 * reserved box with no ad in it is the same broken page by another route.
 */
const reserved: Record<AdSlotFormat, string> = {
  leaderboard: "min-h-[90px]",
  rectangle: "min-h-[250px]",
  responsive: "min-h-[100px]",
};

const dimensions: Record<AdSlotFormat, { w: number; h: number } | null> = {
  leaderboard: { w: 728, h: 90 },
  rectangle: { w: 300, h: 250 },
  responsive: null,
};

/** Dispatched on `window` whenever the consent flag changes. */
export const AD_CONSENT_EVENT = "suite:adconsent";

declare global {
  interface Window {
    adsbygoogle?: unknown[];
    __suiteAdConsent?: boolean;
  }
}

/**
 * The AdSense ad client (`ca-pub-…`), inlined at build time. Exported because
 * adConsent.tsx derives the Funding Choices publisher id from it, so the id
 * lives in exactly one place.
 */
export const AD_CLIENT_ID = import.meta.env["VITE_ADSENSE_CLIENT"] as string | undefined;

/** True once a consent manager has granted advertising consent. */
export function hasAdConsent(): boolean {
  return typeof window !== "undefined" && window.__suiteAdConsent === true;
}

/**
 * Call from the consent manager's callback. Sets the flag *and* notifies any
 * mounted <AdBanner>, so consent granted after first paint still turns ads on.
 */
export function setAdConsent(granted: boolean): void {
  if (typeof window === "undefined") return;
  window.__suiteAdConsent = granted;
  window.dispatchEvent(new Event(AD_CONSENT_EVENT));
}

function useAdConsent(): boolean {
  // Starts false even when consent is already stored, because the home page is
  // prerendered and hydrated: reading the flag during the first render would
  // produce an <ins> the server-rendered HTML does not have. The effect below
  // syncs it immediately after hydration instead.
  const [granted, setGranted] = useState(false);
  useEffect(() => {
    const sync = () => setGranted(hasAdConsent());
    sync();
    window.addEventListener(AD_CONSENT_EVENT, sync);
    return () => window.removeEventListener(AD_CONSENT_EVENT, sync);
  }, []);
  return granted;
}

/**
 * Watches the `data-ad-status` AdSense writes onto the <ins>. "unfilled" means
 * the request finished with no creative — routine when a unit is new, when no
 * inventory matches, or when a blocker intercepts the tag — and it is final for
 * this instance, so collapsing on it does not race a creative that still might
 * arrive. Returns false only once we have actually been told "unfilled".
 */
function useAdUnfilled(node: HTMLModElement | null, enabled: boolean): boolean {
  const [unfilled, setUnfilled] = useState(false);

  useEffect(() => {
    if (!enabled || !node) return;
    const read = () => setUnfilled(node.getAttribute("data-ad-status") === "unfilled");
    read();
    const observer = new MutationObserver(read);
    observer.observe(node, { attributes: true, attributeFilter: ["data-ad-status"] });
    return () => observer.disconnect();
  }, [node, enabled]);

  return unfilled;
}

export interface AdBannerProps {
  slot?: string;
  format?: AdSlotFormat;
  className?: string;
  /** Stable per-route value; changing it remounts the slot on navigation. */
  routeKey?: string;
}

/**
 * AdSense wrapper that is inert until BOTH an ad client is configured and
 * consent has been granted. Until then it renders nothing (no box, no reserved
 * height, no margin). The one exception is dev with a client id configured,
 * where a labelled outline shows where the ad will land.
 *
 * The script is injected lazily rather than in index.html: loading it before
 * consent would itself be the consent violation the CMP exists to prevent.
 */
export function AdBanner({
  slot,
  format = "responsive",
  className,
  routeKey,
}: AdBannerProps) {
  // State, not a ref: useAdUnfilled's effect has to re-run once the <ins> is
  // actually in the DOM, and a ref assignment does not re-render.
  const [node, setNode] = useState<HTMLModElement | null>(null);
  // StrictMode invokes effects twice in dev, and adsbygoogle throws
  // "All ins elements must have a data-ad-status" if the same slot is pushed
  // twice. This latch makes the push happen exactly once per mounted slot.
  const pushed = useRef(false);
  const consented = useAdConsent();
  const enabled = Boolean(AD_CLIENT_ID) && Boolean(slot) && consented;

  useEffect(() => {
    if (!enabled || pushed.current) return;
    if (!node || node.getAttribute("data-ad-status")) return;

    const scriptId = "adsbygoogle-js";
    if (!document.getElementById(scriptId)) {
      const script = document.createElement("script");
      script.id = scriptId;
      script.async = true;
      script.crossOrigin = "anonymous";
      script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${AD_CLIENT_ID}`;
      document.head.appendChild(script);
    }

    pushed.current = true;
    try {
      (window.adsbygoogle = window.adsbygoogle ?? []).push({});
    } catch {
      // A blocked or failed ad must never take the app down with it.
      pushed.current = false;
    }
  }, [enabled, node, routeKey]);

  const unfilled = useAdUnfilled(node, enabled);
  const size = dimensions[format];

  if (!enabled) {
    if (!import.meta.env.DEV || !AD_CLIENT_ID || !slot) return null;
    return (
      <div
        aria-hidden="true"
        className={cn(
          "flex w-full items-center justify-center rounded-lg border border-dashed border-border bg-surface-2/40 text-[10px] font-medium tracking-widest text-fg-muted/60 uppercase",
          reserved[format],
          className,
        )}
      >
        {`ad · ${format} (awaiting consent)`}
      </div>
    );
  }

  return (
    // `hidden` last so it survives cn()'s plain join, and on the wrapper rather
    // than the <ins> so the caller's margin collapses with it — otherwise an
    // unfilled slot still leaves its mt-14 behind as a visible gap. The <ins>
    // stays mounted underneath; AdSense is done with it either way.
    <div className={cn("w-full", reserved[format], className, unfilled && "hidden")}>
      <ins
        ref={setNode}
        key={routeKey}
        className="adsbygoogle block"
        style={
          size
            ? { display: "inline-block", width: size.w, height: size.h }
            : { display: "block" }
        }
        data-ad-client={AD_CLIENT_ID}
        data-ad-slot={slot}
        {...(size ? {} : { "data-ad-format": "auto", "data-full-width-responsive": "true" })}
      />
    </div>
  );
}
